import type { useI18n } from 'vue-i18n';
import type { EngineError, EnginePanicError } from '#layers/base/types/error';
import type { ValueOf } from '#layers/base/types/utils';
import { translateString } from '#layers/i18n/public';
import { maybeResponseError } from './maybe-response-error';
import { isRecord } from './object';

export const ErrorCodes = {
	WORKSPACE_ACCESS_DENIED: 'WORKSPACE_ACCESS_DENIED',
} as const;

export type ErrorCode = ValueOf<typeof ErrorCodes>;

export function hasErrorCode(error: unknown, code: ErrorCode): boolean {
	if (!isRecord(error) || !('data' in error)) return false;

	const { data } = error;

	return isRecord(data) && 'code' in data && data.code === code;
}

/**
 * Formats an error message for display in the UI
 * Translates only strings that come from the Studio, Engine errors should be displayed as is.
 * TODO error codes
 */
export function formatError(error: any, i18n: ReturnType<typeof useI18n>): string {
	if (typeof error === 'string') {
		return translateString(error, i18n);
	}

	if (typeof error === 'object') {
		if (error.data) {
			if (typeof error.data === 'object' && error.data.message) {
				return error.data.message;
			}
			else if (typeof error.data === 'string') {
				return error.data;
			}
		}

		if (error.message) {
			return translateString(error.message, i18n);
		}
	}

	return i18n.t('errors.unknown_error');
}

export function toStructuredError(error: any): EngineError | undefined {
	if (typeof error === 'string') {
		return {
			message: error,
		};
	}
	else if (typeof error === 'object') {
		const errorData: any = maybeResponseError(error);

		if (typeof errorData === 'string') {
			return {
				message: errorData,
			};
		}
		else if (typeof errorData === 'object') {
			return {
				message: errorData.message,
				...errorData,
			};
		}
	}
}

export function getDeepestError(error: any): EngineError | undefined {
	if (typeof error === 'object' && error.source) {
		return getDeepestError(error.source);
	}

	return error;
}

export function isEngineError(error: any): error is EngineError {
	return typeof error === 'object' && 'message' in error && ('source' in error || 'meta' in error);
}

export function isEnginePanicError(error: any): error is EnginePanicError {
	return isEngineError(error) && 'meta' in error && !!error.meta && 'isPanic' in error.meta && !!error.meta.isPanic;
}
